import { pb } from '../lib/pocketbase'

/**
 * Optimistic locking using PocketBase's built-in `updated` timestamp.
 *
 * Before updating, re-fetches the record and compares its `updated` timestamp
 * against the one the client last saw. If another user modified the record
 * in the meantime, throws a conflict error instead of silently overwriting.
 *
 * Usage:
 *   await guardedUpdate('clients', clientId, payload, lastUpdated)
 *
 * @param collection - PocketBase collection name
 * @param id - Record ID
 * @param payload - Fields to update
 * @param lastUpdated - The `updated` timestamp the client last saw (from the entity object)
 * @returns The updated record
 */
export async function guardedUpdate<T extends Record<string, any>>(
  collection: string,
  id: string,
  payload: Record<string, any>,
  lastUpdated?: string,
): Promise<T> {
  if (lastUpdated) {
    const current = await pb.collection(collection).getOne(id, { fields: 'updated' })
    if (current.updated !== lastUpdated) {
      const error = new Error(
        'This record was modified by another user. Please refresh and try again.'
      )
      ;(error as any).status = 409
      ;(error as any).code = 'CONFLICT'
      throw error
    }
  }

  return pb.collection(collection).update<T>(id, payload)
}

export const CONFLICT_MESSAGE =
  'Someone else saved this record after you opened it. Reload to see their version; your unsaved edits will be lost.'

/**
 * True for the error guardedUpdate throws, including after the API layer has
 * rewrapped it (the wrappers copy `status` but not `code`).
 */
export function isConflictError(err: unknown): boolean {
  const e = err as { status?: number; code?: string; message?: string } | null
  if (!e) return false
  return e.status === 409 || e.code === 'CONFLICT' ||
    /modified by another user/i.test(String(e.message ?? ''))
}
